import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

export type UserRole = 'admin' | 'manager' | 'technician' | 'viewer'

export interface Permission {
  canView: boolean
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
}

/**
 * Composable for role-based permissions
 * Provides granular access control based on user roles
 */
export function usePermissions() {
  const authStore = useAuthStore()

  // Get current user role
  const userRole = computed<UserRole>(() => {
    const role = authStore.user?.role?.toLowerCase() || 'viewer'
    return role as UserRole
  })

  // Check if user is viewer
  const isViewer = computed(() => userRole.value === 'viewer')

  // Check if user is admin
  const isAdmin = computed(() => userRole.value === 'admin')

  // Check if user is manager
  const isManager = computed(() => userRole.value === 'manager')

  // Check if user is technician
  const isTechnician = computed(() => userRole.value === 'technician')

  // Check if user can perform CRUD operations
  const canCreate = computed(() => !isViewer.value)
  const canEdit = computed(() => !isViewer.value)
  const canDelete = computed(() => !isViewer.value)

  // Get permissions for a specific resource
  function getPermissions(resource?: string): Permission {
    // Viewers can only view
    if (isViewer.value) {
      return {
        canView: true,
        canCreate: false,
        canEdit: false,
        canDelete: false,
      }
    }

    // Default permissions for non-viewers
    return {
      canView: true,
      canCreate: true,
      canEdit: true,
      canDelete: true,
    }
  }

  // Check if user can access a specific menu item
  function canAccessMenu(menuPath: string): boolean {
    // Viewers cannot access User Management
    if (isViewer.value && menuPath === '/user-permissions') {
      return false
    }

    // All other menus are accessible
    return true
  }

  // Check if user has specific permission
  function hasPermission(permission: 'view' | 'create' | 'edit' | 'delete'): boolean {
    if (isViewer.value && permission !== 'view') {
      return false
    }
    return true
  }

  return {
    userRole,
    isViewer,
    isAdmin,
    isManager,
    isTechnician,
    canCreate,
    canEdit,
    canDelete,
    getPermissions,
    canAccessMenu,
    hasPermission,
  }
}
